import { useEffect, useState } from "react";
import { Input, Modal, message } from "antd";
import { CloseOutlined, PictureOutlined } from "@ant-design/icons";

import { Button } from "../../../../shared";
import type { AdminTag } from "../../../../../queries/casino-configuration/games/fetchAdminTags";
import {
  useCreateAdminTag,
  useUpdateAdminTag,
} from "../../../../../queries/casino-configuration/games/useAdminTags";
import { AppStrings } from "../../../../../utils/constants/AppStrings";

import "../stylesTagsConfiguration.css";

/**
 * Mirrors the BE slug constraint: lowercase letters, digits, underscores.
 * Cap at 64 chars to keep URLs reasonable.
 */
const TAG_ID_PATTERN = /^[a-z0-9_]{1,64}$/;
const HEX_PATTERN = /^#[0-9a-fA-F]{6}$/;
const DEFAULT_COLOR = "#9ea2b8";

export type TagFormModalProps = {
  open: boolean;
  /** When provided, the modal renders in edit mode and seeds the form. */
  tag?: AdminTag | null;
  onClose: () => void;
};

function TagFormModal({ open, tag, onClose }: TagFormModalProps) {
  const isEdit = Boolean(tag);

  const [tagId, setTagId] = useState("");
  const [name, setName] = useState("");
  const [color, setColor] = useState(DEFAULT_COLOR);
  /**
   * Tracks whether the user touched the name / color fields. Used for two
   * things:
   *  1. Inline name-required error only after the user has interacted with
   *     the field (avoids screaming at them the moment they open the modal).
   *  2. PATCH body: when editing a tag that arrived from the backend WITHOUT
   *     a color (e.g. seed-data that pre-dates the color field), we seed the
   *     form with the grey placeholder so the swatch / preview don't break,
   *     but we must NOT persist that placeholder unless the user explicitly
   *     picked a color. `colorTouched` is the guard.
   */
  const [nameTouched, setNameTouched] = useState(false);
  const [colorTouched, setColorTouched] = useState(false);

  const { mutateAsync: createTag, isPending: creating } = useCreateAdminTag();
  const { mutateAsync: updateTag, isPending: updating } = useUpdateAdminTag();
  const saving = creating || updating;

  // Seed / reset the form whenever the modal opens or the edited tag changes.
  useEffect(() => {
    if (!open) return;
    if (tag) {
      setTagId(tag.id);
      setName(tag.name);
      setColor(tag.color || DEFAULT_COLOR);
    } else {
      setTagId("");
      setName("");
      setColor(DEFAULT_COLOR);
    }
    setNameTouched(false);
    setColorTouched(false);
  }, [open, tag]);

  const trimmedId = tagId.trim();
  const trimmedName = name.trim();
  const trimmedColor = color.trim();
  const idValid = isEdit ? true : TAG_ID_PATTERN.test(trimmedId);
  const colorValid = HEX_PATTERN.test(trimmedColor);
  const canSubmit = Boolean(trimmedName) && idValid && colorValid && !saving;

  const previewColor = colorValid ? trimmedColor : DEFAULT_COLOR;
  const previewLabel = (
    trimmedName ||
    trimmedId ||
    AppStrings.CASINO_CONFIG_TAGS_PREVIEW_FALLBACK
  ).toUpperCase();

  const title =
    isEdit && tag
      ? `${AppStrings.CASINO_CONFIG_TAGS_EDIT_TITLE_PREFIX} ${tag.name}`
      : AppStrings.CASINO_CONFIG_TAGS_NEW_TITLE;

  const subtitle = isEdit
    ? AppStrings.CASINO_CONFIG_TAGS_EDIT_SUBTITLE
    : AppStrings.CASINO_CONFIG_TAGS_NEW_SUBTITLE;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    try {
      if (isEdit && tag) {
        // Only include `color` in the PATCH body when the user actually
        // touched the field OR the tag already had a stored color we're
        // safe to round-trip. A colorless tag the admin only renamed must
        // NOT have the placeholder grey persisted as its new color.
        const includeColor = colorTouched || Boolean(tag.color);
        await updateTag({
          tag_id: tag.id,
          name: trimmedName,
          ...(includeColor ? { color: trimmedColor } : {}),
        });
        message.success(AppStrings.CASINO_CONFIG_TAGS_UPDATE_SUCCESS);
      } else {
        await createTag({
          tag_id: trimmedId,
          name: trimmedName,
          color: trimmedColor,
        });
        message.success(AppStrings.CASINO_CONFIG_TAGS_CREATE_SUCCESS);
      }
      onClose();
    } catch (err) {
      const fallback = isEdit
        ? AppStrings.CASINO_CONFIG_TAGS_UPDATE_ERROR
        : AppStrings.CASINO_CONFIG_TAGS_CREATE_ERROR;
      message.error((err as Error)?.message || fallback);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      closeIcon={<CloseOutlined />}
      footer={null}
      width={480}
      className="tag-modal"
      rootClassName="tag-modal-root"
      destroyOnHidden
    >
      <div className="tag-modal-header">
        <h3 className="tag-modal-title">{title}</h3>
        <p className="tag-modal-subtitle">{subtitle}</p>
      </div>

      <div className="tag-modal-body">
        <label className="tag-modal-field">
          <span className="tag-modal-label">
            {AppStrings.CASINO_CONFIG_TAGS_FORM_ID_LABEL}
          </span>
          <Input
            value={tagId}
            disabled={isEdit}
            onChange={(e) =>
              setTagId(e.target.value.toLowerCase().replace(/\s+/g, "_"))
            }
            maxLength={64}
            data-testid="tag-id-input"
          />
          <span className="tag-modal-hint">
            {isEdit
              ? AppStrings.CASINO_CONFIG_TAGS_FORM_ID_HINT_EDIT
              : AppStrings.CASINO_CONFIG_TAGS_FORM_ID_HINT_CREATE}
          </span>
          {!isEdit && trimmedId && !idValid ? (
            <span className="tag-modal-error" role="alert">
              {AppStrings.CASINO_CONFIG_TAGS_FORM_INVALID_SLUG}
            </span>
          ) : null}
        </label>

        <label className="tag-modal-field">
          <span className="tag-modal-label">
            {AppStrings.CASINO_CONFIG_TAGS_FORM_NAME_LABEL}
          </span>
          <Input
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setNameTouched(true);
            }}
            maxLength={64}
            data-testid="tag-name-input"
          />
          {nameTouched && !trimmedName ? (
            <span className="tag-modal-error" role="alert">
              {AppStrings.CASINO_CONFIG_TAGS_FORM_NAME_REQUIRED}
            </span>
          ) : null}
        </label>

        <label className="tag-modal-field">
          <span className="tag-modal-label">
            {AppStrings.CASINO_CONFIG_TAGS_FORM_COLOR_LABEL}
          </span>
          <div className="tag-modal-color-row">
            <Input
              value={color}
              onChange={(e) => {
                setColor(e.target.value.trim());
                setColorTouched(true);
              }}
              maxLength={7}
              data-testid="tag-color-input"
            />
            {/*
             * Native color picker. The text input above accepts direct hex
             * entry; this swatch button lets the admin pick from the system
             * color picker (RGB sliders, hex spectrum). Both write to the same
             * `color` state. `value` falls back to the last-valid `previewColor`
             * so an in-progress invalid hex (e.g. "#zzz") doesn't blow up
             * the picker — but the actual saved value still has to pass the
             * hex regex below.
             */}
            <input
              type="color"
              className="tag-modal-color-swatch"
              aria-label={AppStrings.CASINO_CONFIG_TAGS_FORM_COLOR_LABEL}
              value={previewColor}
              onChange={(e) => {
                setColor(e.target.value);
                setColorTouched(true);
              }}
              data-testid="tag-color-swatch"
            />
          </div>
          <span className="tag-modal-hint">
            {AppStrings.CASINO_CONFIG_TAGS_FORM_COLOR_HINT}
          </span>
          {!colorValid ? (
            <span className="tag-modal-error" role="alert">
              {AppStrings.CASINO_CONFIG_TAGS_FORM_INVALID_COLOR}
            </span>
          ) : null}
        </label>

        <div className="tag-modal-field">
          <span className="tag-modal-label">
            {AppStrings.CASINO_CONFIG_TAGS_FORM_PREVIEW_LABEL}
          </span>
          <div
            className="tag-modal-preview"
            data-testid="tag-preview"
            data-preview-color={previewColor}
          >
            <div
              className="tag-modal-preview-card"
              style={{ borderColor: previewColor }}
            >
              <span
                className="tag-modal-preview-pill"
                style={{
                  background: `${previewColor}22`,
                  color: previewColor,
                  borderColor: previewColor,
                }}
              >
                {`+ ${previewLabel}`}
              </span>
              <PictureOutlined className="tag-modal-preview-icon" />
              <span className="tag-modal-preview-sample">
                {AppStrings.CASINO_CONFIG_TAGS_FORM_PREVIEW_SAMPLE_GAME}
              </span>
            </div>
            <p className="tag-modal-preview-body">
              {AppStrings.CASINO_CONFIG_TAGS_FORM_PREVIEW_BODY}
            </p>
          </div>
        </div>
      </div>

      <div className="tag-modal-footer">
        <Button intent="neutral" onClick={onClose}>
          {AppStrings.CASINO_CONFIG_TAGS_FORM_CANCEL}
        </Button>
        <Button
          intent="primary"
          onClick={() => void handleSubmit()}
          loading={saving}
          disabled={!canSubmit}
          data-testid="tag-submit-button"
        >
          {saving
            ? AppStrings.CASINO_CONFIG_TAGS_FORM_SAVING
            : isEdit
              ? AppStrings.CASINO_CONFIG_TAGS_FORM_SAVE
              : AppStrings.CASINO_CONFIG_TAGS_FORM_CREATE}
        </Button>
      </div>
    </Modal>
  );
}

export default TagFormModal;
